/* ==========================================================================
   Greasy Miniatures — global theme script
   Vanilla JS, no dependencies, no build step.

   Responsibilities at scaffold stage:
     - keep --gm-header-height in sync with the real header
     - own the cart count and refresh it when anything changes the cart
     - collapse the footer's link columns on a phone, and reveal the
       back-to-top button that cannot work without script
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Cart events
     Any component that changes the cart dispatches gm:cart:update on
     document with the cart object as detail. Nothing polls.
     ------------------------------------------------------------------ */

  var CART_UPDATE = 'gm:cart:update';

  function updateCartCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = String(cart.item_count);
      el.hidden = cart.item_count === 0;
    });

    document.querySelectorAll('[data-cart-count-label]').forEach(function (el) {
      el.textContent = el.dataset.cartCountLabel.replace('[count]', cart.item_count);
    });
  }

  document.addEventListener(CART_UPDATE, function (event) {
    updateCartCount(event.detail && event.detail.cart);
  });

  /* ------------------------------------------------------------------
     Header height
     Several layout rules depend on knowing the real header height,
     including sticky product details and scroll-padding for anchors.
     ------------------------------------------------------------------ */

  function trackHeaderHeight() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var apply = function () {
      document.documentElement.style.setProperty(
        '--gm-header-height',
        header.offsetHeight + 'px'
      );
    };

    apply();

    if ('ResizeObserver' in window) {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener('resize', apply, { passive: true });
    }
  }

  /* ------------------------------------------------------------------
     Auto-submitting selects
     Used by the country/currency form. Kept out of the markup so there
     is no inline JavaScript to trip a content security policy.
     ------------------------------------------------------------------ */

  function bindAutoSubmit() {
    document.addEventListener('change', function (event) {
      var el = event.target;
      if (el && el.matches('[data-auto-submit]') && el.form) {
        el.form.submit();
      }
    });
  }

  /* ------------------------------------------------------------------
     Footer link columns
     The columns are <details open> in the markup, and that is the whole
     point: with no script, or before this runs, every link is present.
     What this adds is the phone behaviour — closing them so the footer is
     three tappable rows instead of two screens of links — and it is
     reversed the moment the viewport is wide enough to show the columns
     side by side.

     Only footers marked [data-footer-collapsible] are touched: with one
     column there is nothing to collapse, and greasy-footer.liquid leaves
     the attribute off in that case. A column the visitor opened by hand
     stays as they left it until the breakpoint is crossed again.
     ------------------------------------------------------------------ */

  function bindFooterDisclosures() {
    var footer = document.querySelector('[data-footer-collapsible]');
    if (!footer || !window.matchMedia) return;

    var columns = footer.querySelectorAll('.gm-footer__disclosure');
    if (!columns.length) return;

    var phone = window.matchMedia('(max-width: 767px)');

    var apply = function (event) {
      var collapse = event.matches;
      columns.forEach(function (el) {
        el.open = !collapse;

        /* Out of the tab order when it is not collapsing anything. The CSS
           already blocks the pointer at those widths; without this the
           keyboard could still fold a column away on a desktop, where the
           chevron is hidden and nothing on screen says it could. */
        var summary = el.querySelector('summary');
        if (!summary) return;
        if (collapse) {
          summary.removeAttribute('tabindex');
        } else {
          summary.setAttribute('tabindex', '-1');
        }
      });
    };

    apply(phone);

    /* addListener is the pre-2021 Safari spelling and this is the only
       place in the theme that needs a media query listener, so it is
       cheaper to keep the fallback than to ship a polyfill. */
    if (phone.addEventListener) {
      phone.addEventListener('change', apply);
    } else if (phone.addListener) {
      phone.addListener(apply);
    }
  }

  /* ------------------------------------------------------------------
     Back to top
     Ships hidden and is revealed here. Unlike the disclosures this one
     fails CLOSED: it cannot do anything without script, and a dead
     button is worse than no button.
     ------------------------------------------------------------------ */

  function bindBackToTop() {
    document.querySelectorAll('[data-footer-top]').forEach(function (button) {
      button.hidden = false;
      button.addEventListener('click', function () {
        /* Honour a reduced-motion preference: a full-page smooth scroll is
           exactly the kind of movement that setting is asking about. */
        var reduced =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
      });
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init() {
    trackHeaderHeight();
    bindAutoSubmit();
    bindFooterDisclosures();
    bindBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose a tiny surface for other scripts. */
  window.GreasyTheme = {
    CART_UPDATE: CART_UPDATE,
    emitCartUpdate: function (cart) {
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE, { detail: { cart: cart } })
      );
    }
  };
})();
